import { faXmark as CancelIcon } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useAtom } from 'jotai';
import { useCallback, useEffect, useMemo } from 'react';
import {
  Controller,
  FormProvider,
  type SubmitHandler,
  useForm,
  useWatch
} from 'react-hook-form';

import { TicketCreateAlerts as ServiceFormAlerts } from '@/features/TicketCreateAlerts';
import CHANGE_REQUEST_INTERNAL_NETWORKS_TEMPLATE from '@/features/TicketCreateDialog';
import useStyles from '@/features/TicketCreateDialog/TicketCreateDialog.styles';
import TicketWatchers from '@/features/TicketWatchers';
import { WARNING_MESSAGE_TICKET_WATCHERS_SERVICE } from '@/features/TicketWatchers/TicketWatchers.constants';
import useStaticData from '@/hooks/StaticData.hooks';
import {
  EMPTY_TICKET_DIALOG,
  EMPTY_TICKET_TEMPLATE,
  hasOrganizationWatchers,
  mapWatchersDTO,
  TICKET_TYPE,
  ticketDialogAtom,
  ticketDialogIsOpenAtom,
  ticketDialogTemplateAtom,
  useCreateTicketDialog,
  useFetchWatchersList
} from '@/pages/Ticket';
import type { TicketDialog, Watcher } from '@/pages/Ticket/Ticket.types';
import { isSilencedTicketOrgWatcherService } from '@/pages/Ticket/Ticket.utils';
import { isLayoutMobileAtom } from '@/store/layout';
import { userAtom } from '@/store/user.store';

import { ServiceForm, ServiceFormExtended, ServiceFormInternal } from './forms';
import type { TicketCreateDialogSubmission } from './TicketCreateDialog.types';

const EMPTY_WATCHERS: Watcher[] = [];

const TicketCreateDialog = () => {
  const [isLayoutMobile] = useAtom(isLayoutMobileAtom);
  const [{ ticket_type }, setTicket] = useAtom(ticketDialogAtom);
  const [ticketDialogIsOpen, setTicketDialogIsOpen] = useAtom(
    ticketDialogIsOpenAtom
  );
  const [ticketDialogTemplate, setTicketDialogTemplate] = useAtom(
    ticketDialogTemplateAtom
  );
  const [userData] = useAtom(userAtom);
  const createTicket = useCreateTicketDialog();
  const { categories } = useStaticData();
  const { classes } = useStyles();

  //-----------------------------------------------------------------
  const form = useForm<TicketDialog>({
    defaultValues: EMPTY_TICKET_DIALOG
  });
  const { control, handleSubmit, reset, setValue } = form;

  const ticketCategory = useWatch({ control, name: 'ticket_category' });
  const ticketSubcategory = useWatch({ control, name: 'ticket_subcategory' });
  const ticketWatchers =
    useWatch({ control, name: 'ticket_watchers' }) ?? EMPTY_WATCHERS;
  const ticketSelectedOrganization = useWatch({
    control,
    name: 'organization_id'
  });

  const { watchersDialogOptions: watchersOptions } = useFetchWatchersList(
    'dialog',
    'selectedOrg',
    ticketSelectedOrganization
  );

  const validateWatchers = useCallback(
    (formWatchers: Watcher[]): boolean => {
      const hasOrgWatchers = hasOrganizationWatchers(
        formWatchers.map((w) => w.userId),
        watchersOptions,
        ticketSelectedOrganization
      );

      return hasOrgWatchers;
    },
    [watchersOptions, ticketSelectedOrganization]
  );

  //-----------------------------------------------------------------
  const handelCancel = () => {
    reset();
    setTicket(EMPTY_TICKET_DIALOG);
    setTicketDialogIsOpen(false);
    setTicketDialogTemplate(EMPTY_TICKET_TEMPLATE);
  };

  const onSubmit: SubmitHandler<TicketCreateDialogSubmission> = (data) => {
    // TODO: handle ts error w/ final data
    // @ts-expect-error ts(2345)
    createTicket.mutate({ data, ticketCategory, ticketSubcategory });
    setTicketDialogIsOpen(false);
    setTicketDialogTemplate({
      ...EMPTY_TICKET_TEMPLATE,
      ...CHANGE_REQUEST_INTERNAL_NETWORKS_TEMPLATE
    });
  };

  //-----------------------------------------------------------------
  const silentOrgWatchers = useMemo(() => {
    return ticketWatchers.filter((watcher: Watcher) => {
      return isSilencedTicketOrgWatcherService(ticket_type, watcher);
    });
  }, [ticketWatchers]);

  const watcherCount = useMemo(() => {
    const isDefaultOrgWatchersOptionSelected = ticketWatchers.some(
      (watcher) => watcher.userId === 0
    );

    return isDefaultOrgWatchersOptionSelected
      ? (ticketWatchers?.length ?? 0) - 1
      : (ticketWatchers?.length ?? 0);
  }, [ticketWatchers]);

  useEffect(() => {
    // set the priority to the category's priority (unable to set field value in ui)
    const selectedCategory = categories.find(
      (category) => category.id === (ticketCategory as unknown as number)
    );

    if (selectedCategory && selectedCategory.priority_id !== null) {
      setValue('ticket_priority', selectedCategory.priority_id);
    }
  }, [categories, ticketCategory]);

  useEffect(() => {
    // dialog launch template - loading template
    setValue('attachments', ticketDialogTemplate.attachments || []);
    setValue('summary', ticketDialogTemplate.summary || '');
    setValue('team_queue', ticketDialogTemplate.team_queue || '');
    setValue('ticket_category', ticketDialogTemplate.category || '');
    setValue('title', ticketDialogTemplate.title || '');
  }, [ticketDialogTemplate]);

  useEffect(() => {
    // dialog launch template - loading template w/ category change
    if (
      ticketDialogTemplate.category &&
      ticketDialogTemplate.subcategory &&
      ticketCategory === ticketDialogTemplate.category
    ) {
      setValue('ticket_subcategory', ticketDialogTemplate.subcategory);
    }
  }, [ticketCategory]);

  // priority follows the selected category
  const categoryPriority =
    categories.find((category) => category.id === ticketCategory)
      ?.priority_id ?? null;

  useEffect(() => {
    // set the current user as a watcher when watchers list or organization changes
    // this ensures the current user is always added and stale watchers are cleared
    const mappedWatcherOptions = mapWatchersDTO(watchersOptions);
    const currentUserWatcherObject = mappedWatcherOptions.find(
      (watcher) => watcher.id === userData?.id
    );

    if (currentUserWatcherObject) {
      setValue('ticket_watchers', [currentUserWatcherObject]);
    }
  }, [watchersOptions, userData?.id, setValue]);

  return (
    <Dialog fullWidth open={ticketDialogIsOpen} maxWidth="lg">
      <DialogTitle>Create Ticket</DialogTitle>
      <IconButton
        className={classes.ticketCreateDialogIconButtonCancel}
        onClick={handelCancel}
      >
        <FontAwesomeIcon size="xs" icon={CancelIcon} />
      </IconButton>
      <DialogContent sx={{ pb: 0 }}>
        <FormProvider {...form}>
          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <Stack
              direction={isLayoutMobile ? 'column' : 'row'}
              sx={{ pb: isLayoutMobile ? 3 : 0 }}
            >
              <Stack
                direction="column"
                spacing={2}
                sx={{ py: 2, pb: 3, width: '100%' }}
              >
                {/**
                 * ServiceFormAlerts - displays alerts based on form field values
                 * ServiceForm - standard service ticket form fields
                 * ServiceFormInternal - standard service ticket form fields (internal only)
                 * ServiceFormExtended - additional service ticket form fields (category/subcategory specific)
                 */}
                <ServiceFormAlerts />
                <ServiceForm />
                <ServiceFormInternal />
                <ServiceFormExtended />
              </Stack>
              {!isLayoutMobile && (
                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{ paddingRight: '2rem' }}
                />
              )}
              <Stack
                {...(!isLayoutMobile && {
                  style: { padding: '1rem 2rem', width: '450px' }
                })}
                spacing={2}
              >
                {silentOrgWatchers.length > 0 && (
                  <Alert severity="warning">
                    <AlertTitle>Watchers</AlertTitle>
                    {WARNING_MESSAGE_TICKET_WATCHERS_SERVICE}
                    <ul>
                      {silentOrgWatchers.map((watcher: Watcher) => (
                        <li key={watcher.userId}>{watcher.label}</li>
                      ))}
                    </ul>
                  </Alert>
                )}
                <Typography variant="h6" component="h2" gutterBottom>
                  Watchers ({watcherCount})
                </Typography>
                <Controller
                  control={control}
                  name="ticket_watchers"
                  rules={{
                    validate: validateWatchers
                  }}
                  render={({ field }) => (
                    <TicketWatchers
                      buttonIcon
                      disableOnCreateWithAllOrganizations={
                        ticketSelectedOrganization < 1
                      }
                      onChange={field.onChange}
                      ticketOrganizationId={ticketSelectedOrganization}
                      ticketPriority={categoryPriority}
                      ticketType={TICKET_TYPE.SERVICE}
                      watchers={field.value}
                      watchersOptions={watchersOptions}
                    />
                  )}
                />
              </Stack>
            </Stack>
          </form>
        </FormProvider>
      </DialogContent>
      <DialogActions>
        <Button color="primary" onClick={handelCancel}>
          Cancel
        </Button>
        <Button
          disabled={ticketSelectedOrganization < 1}
          type="submit"
          variant="contained"
          onClick={handleSubmit(onSubmit)}
        >
          Create Ticket
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TicketCreateDialog;
